import { userDashboardLinks } from './navConfig.jsx';

/**
 * Sidebar scopes.
 *
 * <p>A scope is a <b>view over the one nav tree</b>, never a second tree: `userDashboardLinks` is a
 * shared contract (the sidebar, PageHeader's breadcrumb via navLookup/useMenuNavTree, useNavIcon,
 * RequireGameAccess and useRecentPages all resolve against it), so forking it per view would cost
 * scope-only paths their breadcrumb, icon and guard title. Scope sections therefore live in that tree
 * tagged with `scope`, and the functions here select a subtree from it.
 *
 * <p>The hub is the whole tree — the scope sections render there as ordinary sections, which is how
 * you get in. Entering a scope focuses the sidebar on that section alone plus a back row.
 *
 * <p>See docs/nav-scope-alignment.md for why the scope set is what it is (the access model's scope
 * tree is ORG > GAME; `admin` and `portfolio` are presentation views, not permission scopes).
 */

export const DEFAULT_SCOPE = 'hub';

// `root` is the scope's landing path — it also makes the scope resolvable from the URL, so a deep
// link or a refresh lands in the right scope instead of whatever redux last held.
// `headerCards` names the context-switcher cards the top bar shows in that scope, so the header is
// per-view rather than static. Unlisted scopes (and the hub) show none.
// `rootIsLanding` means the bare root is a page in the PARENT view, not the scope: /game lists the
// games and stays on the main nav; you only enter the scope by opening one at /game/<slug>.
export const NAV_SCOPES = {
    // Admin owns every /admin-settings page (org, game and RBAC management) — org-scope
    // administration. Entered from the account popup, not the main nav.
    admin: { label: 'Admin Settings', root: '/admin-settings', headerCards: [] },
    game: { label: 'Game', root: '/game', rootIsLanding: true, headerCards: ['game', 'segment'] },
    portfolio: { label: 'Portfolio', root: '/portfolio', rootIsLanding: true, headerCards: ['portfolio'] },
    // The signed-in user's own page — Profile, whose fourth tab (Platform Settings) holds the
    // workspace preferences. Entered from the account popup like admin, but ROOTLESS: /profile is not
    // a prefix worth reserving, so this scope resolves by MEMBERSHIP (see scopeForPath). Without a
    // scope the page belonged to none at all, so the sidebar had nothing to match it and rendered the
    // hub's unrelated sections while the user sat on it.
    account: { label: 'Account', headerCards: [] },
    // Support + Docs. Its own scope rather than a member of `account`, because product help is not
    // the user's account data — same rootless membership resolution.
    help: { label: 'Help', headerCards: [] },
    // APIs Release + Client SDKs. Its own scope rather than sharing Help's, so a Releases page shows only
    // the Releases section (and a Help page only Help's) — same rootless membership resolution.
    releases: { label: 'Releases', headerCards: [] },
    // Developer Tools (Super User only). Its own scope so /cheats shows its tools, not the member hub it used to
    // fall back to (Homepage, Components, Games, Portfolio).
    devtools: { label: 'Developer Tools', root: '/cheats', headerCards: [] },
};

export const headerCardsFor = (scopeId) => NAV_SCOPES[scopeId]?.headerCards || [];

// Per-page header switcher cards, overriding the scope's default. Keyed by route path (prefix) so
// User/Role management carry their own switchers while Org/Page management show none.
//
// The onboarding wizard shows none. It sets up the ONE game its URL names and selects that game itself
// (see GameOnboarding), so a game card could only offer to switch away mid-setup — fighting the URL —
// and a geography card has nothing to pick from for a game that has no segments yet.
export const PAGE_HEADER_CARDS = {
    '/admin-settings/user-mgmt': ['org'],
    '/admin-settings/role-access-mgmt/role': ['org', 'game', 'segment'],
    '/game/new': [],
    // AI Credits: the org's limits are org-wide; a game's credits are that game's (no segment — the
    // pool is per game). All clients lists every pool and opening one sets the context, so none.
    '/ai-credits/org-limits': ['org'],
    '/ai-credits/game': ['org', 'game'],
    '/ai-credits/clients': [],
};

/**
 * Cards for ONE page, matched exactly rather than by prefix.
 *
 * <p>Needed where a page and its children want different switchers. The games LIST reads its rows
 * for the selected org, so without an org card there is no way to look at another org's games — the
 * defect this entry fixes. Its children ({@code /games/:gameClientId/...}) are a chosen game's
 * workspace, and a prefix match would hand them the same card, so they stay as they were.
 */
export const PAGE_HEADER_CARDS_EXACT = {
    '/admin-settings/game-mgmt/games': ['org'],
};

export const headerCardsForPath = (scopeId, pathname) => {
    if (pathname) {
        // Exact first: a page listed here is opting out of whatever its prefix would give it.
        const exact = PAGE_HEADER_CARDS_EXACT[pathname.replace(/\/$/, '')];
        if (exact) return exact;
        for (const [prefix, cards] of Object.entries(PAGE_HEADER_CARDS)) {
            if (pathname === prefix || pathname.startsWith(prefix + '/')) return cards;
        }
    }
    return headerCardsFor(scopeId);
};

/** The hub sees the whole tree; a scope sees only its own sections. */
export const scopeLinks = (scopeId, links = userDashboardLinks) =>
    scopeId === DEFAULT_SCOPE ? links : (links || []).filter(link => link.scope === scopeId);

/**
 * Whether SOME url resolves to this scope — it declares a root, or the tree lists pages under it.
 * `useNavScope` uses this to decide whether the remembered (redux) scope may be honoured: a scope
 * reachable by URL must never ALSO be remembered, or it follows the user onto pages it does not own.
 * That is exactly how the admin scope used to bleed onto /app-settings, /profile and /help.
 */
export const scopeIsUrlResolvable = (scopeId, links = userDashboardLinks) =>
    !!NAV_SCOPES[scopeId]?.root
    || (links || []).some(l => l.scope === scopeId && (l.path || (l.subItems || []).length));

/** Whether one nav node covers `pathname` — itself, or via a sub-item. */
const claims = (link, pathname) => {
    if (link.path && (pathname === link.path || pathname.startsWith(`${link.path}/`))) return true;
    return (link.subItems || []).some(child => claims(child, pathname));
};

/** The scope that owns `pathname`, or null when no scope claims it (i.e. the hub). */
export const scopeForPath = (pathname, links = userDashboardLinks) => {
    if (!pathname) return null;
    // A scope with a route of its own resolves by prefix — cheap, and independent of the tree.
    for (const [id, def] of Object.entries(NAV_SCOPES)) {
        if (!def.root) continue;
        if (pathname.startsWith(def.root + '/')) return id;
        if (pathname === def.root && !def.rootIsLanding) return id;
    }
    // Otherwise ask the tree, for any scope — a scope's pages need not sit under its root (AI
    // Credits is an admin page at /ai-credits). Anything under a root was already returned above, so
    // the only node that must be skipped here is one pointing AT a `rootIsLanding` root: /game is
    // the Games LIST and belongs to the hub, and you enter the game scope at /game/<slug>, which the
    // prefix loop handles.
    const owner = (links || []).find((l) => {
        if (!l.scope) return false;
        const def = NAV_SCOPES[l.scope];
        if (def?.rootIsLanding && l.path === def.root) return false;
        return claims(l, pathname);
    });
    return owner ? owner.scope : null;
};
